import { useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { Input, Label } from '@/components/ui/primitives';

export interface TagListInputProps {
  id: string;
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  hint?: string;
  validate?: (value: string) => boolean;
  disabled?: boolean;
}

/** A list of short values (emails, phone numbers) entered one at a time — Enter, comma or blur commits. */
export function TagListInput({ id, label, values, onChange, placeholder, hint, validate, disabled }: TagListInputProps) {
  const [draft, setDraft] = useState('');
  const [invalid, setInvalid] = useState(false);

  const commit = (): void => {
    const next = draft.trim();
    if (!next) return;
    if (validate && !validate(next)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    if (!values.includes(next)) onChange([...values, next]);
    setDraft('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commit();
    } else if (event.key === 'Backspace' && draft === '' && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex flex-wrap gap-1.5 rounded-md border bg-muted/30 p-2">
        {values.map((value) => (
          <span key={value} className="flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 font-mono text-xs text-primary">
            {value}
            <button type="button" aria-label={`Remove ${value}`} className="text-primary/70 hover:text-primary" disabled={disabled} onClick={() => onChange(values.filter((v) => v !== value))}>
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <Input
          id={id}
          value={draft}
          placeholder={placeholder}
          disabled={disabled}
          className={invalid ? 'min-w-[12rem] flex-1 border-destructive' : 'min-w-[12rem] flex-1 border-transparent bg-transparent shadow-none'}
          onChange={(e) => {
            setDraft(e.target.value);
            setInvalid(false);
          }}
          onKeyDown={onKeyDown}
          onBlur={commit}
        />
      </div>
      {hint && <p className={invalid ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>{hint}</p>}
    </div>
  );
}
